import { isAfter, isBefore } from 'date-fns';

import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { ApiTypes } from '@mode/shared/contract-common';

export enum MembershipState {
  pending = 'pending',
  banned = 'banned',
  active = 'active',
  requested = 'requested',
}

export interface MembershipResource extends ApiTypes.ModeResource {
  member_token: string;
  admin: boolean;
  avatar: Object;
  email: string;
  limited: boolean;
  name: string;
  state: MembershipState;
  activated_at: Date;
  username: string;
  invited: boolean;
  invite_expires_at: Date;
  managed_by_scim: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace MembershipHelpers {
  import isResource = ModeResourceHelper.isResource;

  export function isPending(resource: MembershipResource): boolean {
    return resource.state === MembershipState.pending;
  }

  export function isActive(resource: MembershipResource): boolean {
    return resource.state === MembershipState.active;
  }

  export function isAdmin(resource: MembershipResource): boolean {
    return resource.admin === true;
  }

  export function isBanned(resource: MembershipResource): boolean {
    return resource.state === MembershipState.banned;
  }

  export function isInviteCancelled(resource: MembershipResource): boolean {
    //For performance purpose, the invited property from membership representer is only present for banned members
    return resource.invited === true;
  }

  export function isLimited(resource: MembershipResource): boolean {
    return isResource(resource) && resource.hasOwnProperty('limited') && resource.limited;
  }

  export function isRequested(resource: MembershipResource): boolean {
    return resource.state === MembershipState.requested;
  }

  export function isManagedByScim(resource: MembershipResource): boolean {
    return resource.managed_by_scim === true;
  }

  export function hasActivatedAt(resource: MembershipResource): boolean {
    return isBefore(resource.activated_at, new Date());
  }

  export function hasValidInvite(resource: MembershipResource): boolean {
    return isAfter(resource.invite_expires_at, new Date());
  }

  export function hasExpiredInvite(resource: MembershipResource): boolean {
    return isBefore(resource.invite_expires_at, new Date());
  }

  export function userLink(resource: MembershipResource): string | null {
    return (isResource(resource) && resource.hasLink('user') && resource.links('user').href) || null;
  }

  export function getWebSelfLink(resource: MembershipResource): string | null {
    return (isResource(resource) && resource.hasLink('web_self') && resource.links('web_self').href) || null;
  }

  export function getWebSelfUnbanLink(resource: MembershipResource): string | null {
    return (
      (isResource(resource) && resource.hasLink('web_self_unban') && resource.links('web_self_unban').href) || null
    );
  }

  export function getWebSelfApproveLink(resource: MembershipResource): string | null {
    return (
      (isResource(resource) && resource.hasLink('web_self_approve') && resource.links('web_self_approve').href) || null
    );
  }

  export function getProfileDownloadLink(resource: MembershipResource): string | null {
    return (
      (isResource(resource) &&
        resource.hasLink('web_self_download_profile') &&
        resource.links('web_self_download_profile').href) ||
      null
    );
  }

  export function getWebConfirmDestroyLink(resource: MembershipResource): string | null {
    return (
      (isResource(resource) &&
        resource.hasLink('web_self_confirm_destroy') &&
        resource.links('web_self_confirm_destroy').href) ||
      null
    );
  }

  export function getWebSelfResendLink(resource: MembershipResource): string | null {
    return (
      (isResource(resource) && resource.hasLink('web_self_resend') && resource.links('web_self_resend').href) || null
    );
  }

  export function canBeMadeAdmin(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isActive(resource) && !isLimited(resource) && !isAdmin(resource);
  }

  export function canRemoveAdmin(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isAdmin(resource);
  }

  export function canDisableMembership(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isActive(resource);
  }

  export function canReInvite(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isBanned(resource);
  }

  export function canReSendInvite(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isPending(resource) && !!resource.invite_expires_at;
  }

  export function canCancelInvite(resource: MembershipResource): boolean {
    return isPending(resource) && !!resource.invite_expires_at;
  }

  export function canApproveOrDecline(resource: MembershipResource): boolean {
    return !isManagedByScim(resource) && isRequested(resource);
  }

  export function canDownloadProfile(resource: MembershipResource): boolean {
    return (isActive(resource) || isBanned(resource)) && hasActivatedAt(resource);
  }

  export function canDeleteUserData(resource: MembershipResource): boolean {
    return isBanned(resource) && hasActivatedAt(resource);
  }
}
